/**
 * The policy surface's calls — `/policy`, `/policy/:slug` and H2's `/policy/refund/:key`.
 * Every one goes through `../../api/client.js` and nothing else.
 *
 * ── WHY THE DOCUMENTS ARE FETCHED AND NOT BUNDLED ───────────────────────────────
 * The full argument is in `apps/api/src/policy/documents/index.ts`, and the short form
 * is: a consent row names a version, so exactly one copy of that version's text may
 * exist in the running system. If the prose were also in this bundle there would be two,
 * deployed on two cadences, free to disagree about what `0.1.0-draft` says — which is
 * precisely the disagreement a consent record exists to rule out.
 *
 * The cost is stated rather than hidden: with no network, a signed-out visitor cannot
 * read the terms, and these screens reach `E_OFFLINE`'s drawn state through
 * `states/boundary.tsx` like every other wired screen. They do NOT fall back to a
 * bundled copy — `routes/explore/api.ts` names that failure exactly: "a screen that
 * quietly substitutes design data for live data is a screen that looks correct in review
 * and lies in production."
 *
 * ── THERE IS NO BANGLA IN THIS DIRECTORY, AND THAT IS CHECKED ───────────────────
 * No design frame is drawn for a policy-document screen — `docs/11-screen-map.md` has
 * one policy row and it is H2's refund sheet — so under the house rule there is no
 * source for a Bangla string here. Every word on both screens, including the titles, the
 * labels and the draft badge, arrives in `chrome` on these responses.
 * `__tests__/policy.test.tsx` scans the components' own source and fails on any Bangla
 * character, so "the client never composes Bangla" is measured rather than promised.
 */
import { ApiError, api } from '../../api/client.js';

/** The four documents, as a closed set. Mirrors `apps/api/src/policy/types.ts`. */
export const POLICY_SLUGS = [
  'terms-of-service',
  'privacy-policy',
  'refund-and-returns',
  'vendor-agreement',
] as const;
export type PolicySlug = (typeof POLICY_SLUGS)[number];

export function isPolicySlug(value: string | undefined): value is PolicySlug {
  return value !== undefined && (POLICY_SLUGS as readonly string[]).includes(value);
}

/** Every label these two screens render. All of it server-supplied. */
export interface PolicyChrome {
  index_title_bn: string;
  index_title_en: string;
  index_intro_bn: string;
  draft_badge_bn: string;
  version_label_bn: string;
  effective_label_bn: string;
  slots_heading_bn: string;
  slots_note_bn: string;
  review_heading_bn: string;
}

export interface PolicyClause {
  id: string;
  heading_bn: string;
  heading_en: string;
  body_bn: string[];
  body_en: string[];
}

export interface PolicySlot {
  key: string;
  label_bn: string;
  label_en: string;
  source_en: string;
}

export interface PolicyDocument {
  slug: PolicySlug;
  version: string;
  effective_from: string;
  review_status: string;
  title_bn: string;
  title_en: string;
  summary_bn: string;
  summary_en: string;
  /** The line the reader must see before any clause. Never optional, never omitted. */
  draft_notice_bn: string;
  draft_notice_en: string;
  review_note_bn: string;
  review_note_en: string;
  clauses: PolicyClause[];
  contact: {
    heading_bn: string;
    heading_en: string;
    body_bn: string[];
    body_en: string[];
  };
  slots: PolicySlot[];
}

export interface PolicyDocumentSummary {
  slug: PolicySlug;
  version: string;
  effective_from: string;
  review_status: string;
  title_bn: string;
  title_en: string;
  summary_bn: string;
  summary_en: string;
  /** `/policy/terms-of-service`. Server-supplied so the client never builds a path. */
  to: string;
}

export interface PolicyIndexResponse {
  items: PolicyDocumentSummary[];
  digests: Record<string, string>;
  chrome: PolicyChrome;
}

export interface PolicyDocumentResponse {
  document: PolicyDocument;
  content_sha256: string;
  chrome: PolicyChrome;
  to: string;
}

/**
 * `GET /v1/policy/documents` — the index.
 *
 * `anonymous: true` on both calls below. Not an optimisation: a signed-out visitor is
 * the CASE these routes exist for, and sending a stale token on a route that ignores it
 * would make an expired session look like a reason the terms cannot be read.
 */
export function fetchPolicyIndex(signal?: AbortSignal): Promise<PolicyIndexResponse> {
  return api.get<PolicyIndexResponse>('/v1/policy/documents', { signal, anonymous: true });
}

/** `GET /v1/policy/documents/:slug` — the version in force. */
export function fetchPolicyDocument(
  slug: PolicySlug,
  signal?: AbortSignal,
): Promise<PolicyDocumentResponse> {
  return api.get<PolicyDocumentResponse>(`/v1/policy/documents/${encodeURIComponent(slug)}`, {
    signal,
    anonymous: true,
  });
}

/**
 * `GET /v1/policy/documents/:slug/:version` — one exact published version.
 *
 * What a consent record points at. Not called by either screen today: nothing in the
 * product yet shows a person the version they agreed to, which is an account-screen row
 * this stream does not own. Written here because the route exists and because the next
 * person to need it should not have to rediscover that it does — see
 * `docs/bangla-needed/legal-documents.md § what is still open`.
 */
export function fetchPolicyDocumentVersion(
  slug: PolicySlug,
  version: string,
  signal?: AbortSignal,
): Promise<PolicyDocumentResponse> {
  return api.get<PolicyDocumentResponse>(
    `/v1/policy/documents/${encodeURIComponent(slug)}/${encodeURIComponent(version)}`,
    { signal, anonymous: true },
  );
}

/* ─────────────────────────────────────────────────────── H2 · the refund terms ── */

/** Whether a piece of H2's text is transcribed from the frame or written by the build. */
export type RefundCopyStatus = 'sourced' | 'authored';

export interface RefundCondition {
  /** The drawn ordinal, in Bangla numerals: ১, ২, ৩. */
  ordinal_bn: string;
  title_bn: string;
  body_bn: string;
  copy_status: RefundCopyStatus;
}

/**
 * `GET /v1/policy/refund/:key` — everything H2 renders, and nothing it must compose.
 * Mirrors `apps/api/src/policy/refund-terms.ts § RefundTermsResponse`.
 */
export interface RefundTermsResponse {
  key: string;
  title_bn: string;
  title_copy_status: RefundCopyStatus;
  intro_bn: string;
  /** Always three, in H2's order: when, what evidence, how the money comes back. */
  conditions: RefundCondition[];
  /** H2's closing note — null for a key no support problem opens, and then not drawn. */
  note_bn: string | null;
  ack_bn: string;
  backdrop_title_bn: string;
  window_minutes: number;
  policy: { slug: 'refund-and-returns'; version: string; review_status: string; to: string };
}

/**
 * `GET /v1/policy/refund/:key` — the terms one checkout quote's `refund_policy_key` names.
 *
 * ── WHY THE TERMS ARE FETCHED AND NO LONGER BUNDLED ─────────────────────────────
 * They were a constant in this directory (`data.ts`) for ONE key, so every other key a
 * quote carried opened nothing — and the constant's third condition promised a refund
 * into অপ্রাপ্য ক্যাশ, which the founders withdrew on 2026-09-18. The terms live in the
 * `refund_policies` row the checkout links to (docs/03: "two screens, one source"), and
 * how the money comes back is a sentence of the refund policy the API serves. Two copies
 * — one here, one there — is how the sheet came to promise what the policy no longer did.
 *
 * `anonymous: true`, as for the documents: the terms of a refund are readable before
 * sign-in (H1 opens them from a sheet a signed-out visitor reads), and a stale token must
 * not make an expired session look like a reason they cannot be read.
 *
 * ── A 404 IS AN ANSWER, NOT A FAILURE ───────────────────────────────────────────
 * It means "no terms are written for this key" — a key no row defines, or a row whose
 * terms are still 0300's placeholder. That resolves to `null` here, so H2 renders no
 * sheet and reports the gap, exactly as it did before the terms were served. Every other
 * failure — offline, the store unbound — is thrown for the state layer to draw.
 */
export async function fetchRefundTerms(
  key: string,
  signal?: AbortSignal,
): Promise<RefundTermsResponse | null> {
  try {
    return await api.get<RefundTermsResponse>(`/v1/policy/refund/${encodeURIComponent(key)}`, {
      signal,
      anonymous: true,
    });
  } catch (cause: unknown) {
    if (cause instanceof ApiError && cause.status === 404) return null;
    throw cause;
  }
}
